/* Prices and inventory remain Shopify-owned; these helpers only present returned data. */
const AtlasCommerceRules = {
  orderedKeys(previous, current) {
    return [...previous.filter(key => current.includes(key)), ...current.filter(key => !previous.includes(key))];
  },
  imageBounds(box, width, height) {
    const scale = Math.min(box.width / width, box.height / height);
    const w = width * scale, h = height * scale;
    return { left: box.left + (box.width - w) / 2, top: box.top + (box.height - h) / 2, width: w, height: h };
  }
};
if (typeof document !== 'undefined') {
  const storage = {
    get(key, fallback) { try { return JSON.parse(localStorage.getItem(key)) ?? fallback; } catch { return fallback; } },
    set(key, value) { try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* Browsing still works without storage. */ } }
  };
  const session = {
    get(key, fallback) { try { return JSON.parse(sessionStorage.getItem(key)) ?? fallback; } catch { return fallback; } },
    set(key, value) { try { sessionStorage.setItem(key, JSON.stringify(value)); } catch { /* Keep current-page state. */ } }
  };
  class AtlasCartShelf extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.order = session.get('atlas-cart-order', []);
      if (!Array.isArray(this.order)) this.order = [];
      this.active = session.get('atlas-cart-visible', false);
      this.sync();
      this.addEventListener('click', event => {
        if (event.target.closest('[data-shelf-close]')) {
          this.active = false; session.set('atlas-cart-visible', false); this.sync();
        }
        const move = event.target.closest('[data-shelf-move]');
        if (move) this.querySelector('.atlas-cart-shelf__track').scrollBy({left: Number(move.dataset.shelfMove) * 240, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'});
      });
      this.addEventListener('keydown', event => {
        if (!event.target.matches('.atlas-cart-shelf__track')) return;
        if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
          event.preventDefault(); event.target.scrollBy({left:event.key === 'ArrowRight' ? 240 : -240});
        }
      });
      this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, event => {
        if (event.source !== 'product-form') return this.refresh();
      });
    }
    disconnectedCallback() { this.unsubscribe?.(); this.ready = false; }
    getSectionsToRender() { return [{id:'atlas-cart-shelf'}, {id:'cart-icon-bubble'}]; }
    setActiveElement(element) { this.activeElement = element; }
    renderContents(response) {
      this.active = true; session.set('atlas-cart-visible', true);
      this.replaceSections(response.sections);
      if (!response.sections?.['atlas-cart-shelf']) this.refresh();
      this.sync(response.variant_id || response.id);
      const status = this.querySelector('[data-shelf-status]');
      if (status) status.textContent = 'Ürün sepete eklendi.';
      document.querySelector('[data-atlas-commerce-status]').textContent = 'Ürün sepete eklendi. Alışverişe devam edebilir veya sepete gidebilirsiniz.';
    }
    replaceSections(sections) {
      if (sections?.['atlas-cart-shelf']) {
        const incoming = new DOMParser().parseFromString(sections['atlas-cart-shelf'], 'text/html').querySelector('atlas-cart-shelf');
        if (incoming) this.innerHTML = incoming.innerHTML;
      }
      if (sections?.['cart-icon-bubble']) {
        const icon = document.querySelector('#cart-icon-bubble');
        const content = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html').querySelector('.shopify-section');
        if (icon && content) icon.innerHTML = content.innerHTML;
      }
    }
    async refresh() {
      try {
        const response = await fetch(`${routes.cart_url}?sections=atlas-cart-shelf,cart-icon-bubble`);
        if (!response.ok) throw new Error('Cart refresh failed');
        this.replaceSections(await response.json()); this.sync();
      } catch { /* Native cart remains the authoritative view after a refresh failure. */ }
    }
    sync(variant) {
      const content = this.querySelector('.atlas-cart-shelf__content');
      const track = this.querySelector('.atlas-cart-shelf__track');
      if (!content || !track) return;
      const items = [...track.children];
      // Shopify returns newest lines first; the shelf preserves first-added order across navigation.
      this.order = AtlasCommerceRules.orderedKeys(this.order, items.map(item => item.dataset.key).reverse());
      session.set('atlas-cart-order', this.order);
      this.order.forEach(key => track.append(items.find(item => item.dataset.key === key)));
      this.hidden = !this.active || Number(content.dataset.count) === 0 || this.dataset.cartPage === 'true';
      document.body.classList.toggle('atlas-shelf-visible', !this.hidden);
      const target = variant && items.find(item => item.dataset.variant === String(variant));
      if (target && !this.hidden) track.scrollLeft = Math.max(0, target.offsetLeft - track.offsetLeft);
    }
  }
  if (!customElements.get('atlas-cart-shelf')) customElements.define('atlas-cart-shelf', AtlasCartShelf);

  let favorites = storage.get('atlas-favorites', {});
  if (!favorites || typeof favorites !== 'object' || Array.isArray(favorites)) favorites = {};
  const favoriteDialog = document.querySelector('[data-atlas-favorites-dialog]');
  function syncFavorites() {
    document.querySelectorAll('[data-atlas-favorite]').forEach(button => {
      const saved = Boolean(favorites[button.dataset.atlasFavorite]);
      const label = `${button.dataset.title} ${saved ? 'favorilerden çıkar' : 'favorilere ekle'}`;
      // Avoid waking the PDP purchase observer when the favorite state has not changed.
      if (button.getAttribute('aria-pressed') !== String(saved)) button.setAttribute('aria-pressed', String(saved));
      if (button.getAttribute('aria-label') !== label) button.setAttribute('aria-label', label);
    });
  }
  function renderFavorites() {
    const list = favoriteDialog.querySelector('[data-favorites-list]'); list.replaceChildren();
    const items = Object.entries(favorites);
    if (!items.length) { const p = document.createElement('p'); p.textContent = 'Henüz ürün kaydetmedin. Ürünlerdeki kalp simgesine dokun.'; list.append(p); }
    for (const [handle, item] of items) {
      if (!item || typeof item.title !== 'string' || typeof item.url !== 'string' || !item.url.startsWith('/products/')) continue;
      const row = document.createElement('div'); row.className = 'atlas-favorite-row';
      const link = document.createElement('a'); link.href = item.url; link.textContent = item.title;
      const remove = document.createElement('button'); remove.type = 'button'; remove.className = 'atlas-icon-button'; remove.textContent = '×'; remove.setAttribute('aria-label', `${item.title} kayıtlardan çıkar`);
      remove.addEventListener('click', () => { delete favorites[handle]; storage.set('atlas-favorites', favorites); syncFavorites(); renderFavorites(); });
      row.append(link, remove); list.append(row);
    }
  }
  document.addEventListener('click', event => {
    const favorite = event.target.closest('[data-atlas-favorite]');
    if (favorite) {
      const handle = favorite.dataset.atlasFavorite;
      if (favorites[handle]) delete favorites[handle];
      else favorites[handle] = {title:favorite.dataset.title, url:favorite.dataset.url};
      storage.set('atlas-favorites', favorites); syncFavorites();
      document.querySelector('[data-atlas-commerce-status]').textContent = favorites[handle] ? 'Ürün bu cihazda kaydedildi.' : 'Ürün kayıtlardan çıkarıldı.';
    }
    if (event.target.closest('[data-favorites-open]')) { renderFavorites(); favoriteDialog.showModal(); }
    if (event.target.closest('[data-favorites-close]')) favoriteDialog.close();
  });
  window.addEventListener('storage', () => { favorites = storage.get('atlas-favorites', {}); if (!favorites || typeof favorites !== 'object' || Array.isArray(favorites)) favorites = {}; syncFavorites(); });
  syncFavorites();
  new MutationObserver(syncFavorites).observe(document.querySelector('#MainContent'), {childList:true, subtree:true});

  // A separate pointer-driven panel leaves the original image and native lightbox intact.
  const panel = document.createElement('div'); panel.className = 'atlas-zoom-panel'; panel.hidden = true; panel.setAttribute('aria-hidden', 'true'); document.body.append(panel);
  let zoomSource;
  const finePointer = matchMedia('(min-width: 990px) and (hover: hover) and (pointer: fine)');
  document.addEventListener('pointermove', event => {
    const source = event.target.closest('[data-atlas-zoom]');
    if (!source || !finePointer.matches || source.closest('quick-add-modal') || document.querySelector('product-modal[open]')) { panel.hidden = true; zoomSource?.classList.remove('atlas-zoom-active'); zoomSource = null; return; }
    const image = source.querySelector('.product__media img');
    if (!image) return;
    const bounds = AtlasCommerceRules.imageBounds(image.getBoundingClientRect(), Number(source.dataset.sourceWidth), Number(source.dataset.sourceHeight));
    const x = (event.clientX - bounds.left) / bounds.width, y = (event.clientY - bounds.top) / bounds.height;
    const scale = Math.min(2.5, Math.min(2400, Number(source.dataset.sourceWidth)) / bounds.width);
    if (x < 0 || x > 1 || y < 0 || y > 1 || scale <= 1.05) { panel.hidden = true; zoomSource?.classList.remove('atlas-zoom-active'); return; }
    if (zoomSource !== source) { zoomSource?.classList.remove('atlas-zoom-active'); zoomSource = source; panel.style.backgroundImage = `url("${source.dataset.zoomSrc}")`; }
    const box = source.getBoundingClientRect(), size = Math.min(440, innerWidth * .36, innerHeight - 48);
    panel.style.width = panel.style.height = `${size}px`;
    panel.style.left = `${Math.min(innerWidth - size - 16, box.right + 16)}px`;
    panel.style.top = `${Math.max(16, Math.min(box.top, innerHeight - size - 16))}px`;
    const w = bounds.width * scale, h = bounds.height * scale;
    panel.style.backgroundSize = `${w}px ${h}px`;
    panel.style.backgroundPosition = `${size / 2 - x * w}px ${size / 2 - y * h}px`;
    source.style.setProperty('--zoom-x', `${event.clientX - box.left}px`); source.style.setProperty('--zoom-y', `${event.clientY - box.top}px`);
    source.classList.add('atlas-zoom-active'); panel.hidden = false;
  }, {passive:true});
  const hideZoom = () => { panel.hidden = true; zoomSource?.classList.remove('atlas-zoom-active'); zoomSource = null; };
  document.addEventListener('scroll', hideZoom, {passive:true, capture:true}); window.addEventListener('blur', hideZoom); document.addEventListener('pointerleave', hideZoom);
  document.addEventListener('keydown', event => { if(event.key === 'Escape') hideZoom(); });
}
